import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase-client';
// import { Session, User } from '@supabase/supabase-js'; // Remove unused User
import { Session, AuthChangeEvent } from '@supabase/supabase-js'; // Keep Session, add AuthChangeEvent
import Loading from '../components/ui/Loading';

// Define UserProfile type based on your user data needs
export interface UserProfile {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  profileImage?: string;
  phoneNumber?: string;
  gender?: string;
  dateOfBirth?: string;
  rank?: string;
  unit?: string;
  baseLocation?: string;
  medicalCategory?: string;
}

// Registration data type
export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  phoneNumber: string;
}

// Define the shape of the context value
export interface AuthContextType {
  session: Session | null;
  user: UserProfile | null;
  isLoading: boolean;
  isProfileLoading: boolean;
  signOut: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  error: string | null;
}

// Create the context with a default undefined value
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Tracks initial session check
  const [isProfileLoading, setIsProfileLoading] = useState(false); // Tracks profile loading
  const [error, setError] = useState<string | null>(null);
  // Use refs to track session and prevent unnecessary re-renders or duplicate fetches
  const sessionRef = useRef<string | null>(null);
  const initialLoadCompleted = useRef(false);
  // const navigate = useNavigate(); // Removed
  // const location = useLocation(); // Removed

  // Fetch profile data from user_profiles table with a 5-second timeout
  const fetchUserProfile = useCallback(async (userId: string, email?: string | null): Promise<UserProfile | null> => {
    if (!userId) return null;

    console.log('Fetching profile for user ID:', userId);

    // Minimal fallback profile built from auth session data alone
    const minimalProfile: UserProfile = {
      id: userId,
      email: email ?? undefined,
      firstName: '',
      lastName: '',
    };

    try {
      // Race the DB fetch against a 5-second timeout so a missing/slow table
      // can never block the loading state indefinitely.
      const fetchPromise = supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timed out after 5 seconds')), 5000)
      );

      const { data, error: fetchError } = await Promise.race([fetchPromise, timeoutPromise]);

      if (fetchError) {
        console.warn('Could not fetch user profile from DB:', fetchError.message);
        return minimalProfile;
      }

      const profileData: UserProfile = {
        id: data.id,
        email: data.email || email || undefined,
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        phoneNumber: data.phone_number || undefined,
        gender: data.gender || undefined,
        dateOfBirth: data.date_of_birth || undefined,
        rank: data.rank || undefined,
        unit: data.unit || undefined,
        baseLocation: data.base_location || undefined,
        medicalCategory: data.medical_category || undefined,
      };
      console.log('Finished fetching profile:', profileData);
      return profileData;
    } catch (error) {
      console.warn('Profile fetch failed or timed out — using minimal profile:', error);
      return minimalProfile;
    }
  }, []);

  // Check session on initial load and setup auth state listener
  useEffect(() => {
    // Skip if we've already completed initial load
    if (initialLoadCompleted.current) return;

    const checkSession = async () => {
      try {
        console.log('Checking initial session...');
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Error getting initial session:', sessionError);
          setError('Could not fetch session details.');
          setSession(null);
          setUser(null);
          return;
        }

        // Update session ref to prevent duplicate handling in listener
        sessionRef.current = currentSession?.access_token || null;
        setSession(currentSession);

        if (currentSession?.user) {
          console.log('Initial session exists, starting user profile fetch...');
          setIsProfileLoading(true);
          try {
            const profile = await fetchUserProfile(currentSession.user.id, currentSession.user.email);
            setUser(profile);
          } catch (profileError) {
            console.error('Error fetching profile during initial session check:', profileError);
            // Fall back to minimal profile so the user is not left in a broken state
            setUser({
              id: currentSession.user.id,
              email: currentSession.user.email ?? undefined,
              firstName: '',
              lastName: '',
            });
          } finally {
            console.log('Initial profile fetch finished.');
            setIsProfileLoading(false);
          }
        } else {
          console.log('No initial session found');
          setUser(null);
          setIsProfileLoading(false);
        }
      } catch (e) {
        console.error('Exception during initial session check:', e);
        setError('An unexpected error occurred checking session.');
        setSession(null);
        setUser(null);
        setIsProfileLoading(false);
      } finally {
        // ALWAYS resolve the loading gate — regardless of any error path above
        console.log('Initial session check complete, setting isLoading to false');
        setIsLoading(false);
        initialLoadCompleted.current = true;
      }
    };

    checkSession();
  }, [fetchUserProfile]); // fetchUserProfile is stable

  // Set up auth state change listener separately
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(
      async (_event: AuthChangeEvent, newSession: Session | null) => {
        const newToken = newSession?.access_token || null;
        if (sessionRef.current === newToken) {
          console.log('Auth event received but session token unchanged - skipping');
          return;
        }

        console.log('Auth state changed. New session:', newSession ? 'Exists' : 'Null');
        sessionRef.current = newToken;
        setSession(newSession);

        const currentUserId = user?.id; // Get user *before* potential async fetch
        if (newSession?.user) {
          // Only fetch profile if user ID changed
          if (currentUserId !== newSession.user.id) {
            console.log('User ID changed, fetching profile...');
            setIsProfileLoading(true); // Start profile loading
            try {
              const profile = await fetchUserProfile(newSession.user.id, newSession.user.email);
              setUser(profile);
            } catch (error) {
              console.error('Error fetching user profile in auth state change:', error);
              setUser(null); // Set user null on error
            } finally {
              console.log('Profile fetch finished in auth state change.');
              setIsProfileLoading(false); // Finish profile loading
            }
          } else {
             console.log('User ID same as before, profile not re-fetched.');
          }
        } else {
          // Session is null (user logged out)
          setUser(null);
          setIsProfileLoading(false); // Ensure profile loading is false on logout
        }
        // REMOVED setIsLoading calls from here
      }
    );

    return () => {
      data?.subscription?.unsubscribe();
    };
    // user is removed from dependency array as it's read sync before async profile fetch
  }, [fetchUserProfile]);

  const signOut = async () => {
    // Keep isLoading true during signout operation
    setIsLoading(true);
    setIsProfileLoading(false); // Reset profile loading on sign out
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out.');
    } else {
      sessionRef.current = null;
      setSession(null);
      setUser(null);
      // Clear AI assistant conversation (PHI compliance)
      try {
        const { useAIAssistantStore } = await import('../stores/ai-assistant-store');
        useAIAssistantStore.getState().clearConversation();
      } catch {
        // Store may not be initialized yet - safe to ignore
      }
    }
    setIsLoading(false);
  };

  const login = async (email: string, password: string) => {
    setError(null);
    setIsLoading(true); // Use main loading for login process
    setIsProfileLoading(false); // Reset profile loading

    if (!email || !password) {
      setError('Email and password are required');
      setIsLoading(false);
      return;
    }
    
    try {
      console.log(`Attempting login for ${email} with password length: ${password.length}`);
      
      // Check if Supabase is properly configured
      if (!supabase) {
        throw new Error('Supabase client is not initialized properly');
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('Login error details:', {
          message: error.message,
          status: error.status,
          name: error.name
        });
        
        // Provide more user-friendly error messages
        if (error.message.includes('Failed') || error.message.includes('network')) {
          setError('Connection error: Could not connect to authentication service. Please check your internet connection and try again.');
        } else if (error.message.includes('Invalid login')) {
          setError('Invalid email or password. Please try again. Note: If this is a new account, ensure it has been activated.');
        } else {
          setError(error.message);
        }
        return;
      }
      
      console.log('Login successful', data);
      sessionRef.current = data.session?.access_token || null;
      setSession(data.session);
      
      if (data.user) {
        console.log(`Real login: Fetching profile for user ${data.user.id}`);
        setIsProfileLoading(true);
        const profile = await fetchUserProfile(data.user.id, data.user.email);
        setUser(profile);
        setIsProfileLoading(false);
        console.log('Real login: Profile fetch complete.');
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'An unexpected error occurred during login';
      console.error('Unexpected login error:', errorMessage);
      console.error('Full error object:', e);
      
      if (errorMessage.includes('NetworkError') || errorMessage.includes('Failed to fetch')) {
        setError('Network error: Could not connect to the authentication service. Please check your Supabase URL and API key configuration.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false); // Set main loading false AFTER login attempt and profile fetch
    }
  };

  const register = async (data: RegisterData) => {
    setError(null);
    setIsLoading(true);

    try {
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            gender: data.gender,
            date_of_birth: data.dateOfBirth,
            phone_number: data.phoneNumber,
          },
        },
      });

      if (signUpError) {
        console.error('Registration error:', signUpError);
        setError(signUpError.message);
        throw signUpError;
      }

      if (!signUpData.user) {
        const msg = 'Registration failed: no user returned';
        setError(msg);
        throw new Error(msg);
      }

      // The handle_new_user trigger auto-creates the user_profiles row.
      // Update it with phone_number which the trigger may not have set.
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ phone_number: data.phoneNumber })
        .eq('id', signUpData.user.id);

      if (updateError) {
        console.warn('Could not update phone_number on profile:', updateError.message);
      }

      console.log('Registration successful for:', data.email);
    } catch (e) {
      if (!(e instanceof Error && e.message === error)) {
        const msg = e instanceof Error ? e.message : 'Registration failed';
        setError(msg);
      }
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    session,
    user,
    isLoading,
    isProfileLoading,
    signOut,
    login,
    register,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to access the authentication context
// Export the hook correctly
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthRequired component to protect routes
interface AuthRequiredProps {
  children: JSX.Element;
}

export const AuthRequired = ({ children }: AuthRequiredProps): JSX.Element | null => {
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectAttempted = useRef(false);
  const currentPath = location.pathname;

  useEffect(() => {
    // Avoid excessive logging
    if (process.env.NODE_ENV === 'development') {
      console.log('AuthRequired state:', {
        isLoading, // Reflects initial session check ONLY now
        hasSession: !!session,
        currentPath
      });
    }
    
    // Only redirect if we're not loading, there's no session, and we haven't already tried to redirect
    if (!isLoading && !session && !redirectAttempted.current) {
      console.log('No authenticated session, redirecting to login from:', currentPath);
      // Set flag to prevent multiple redirects
      redirectAttempted.current = true;
      // Use state to remember where user came from for redirect after login
      navigate('/login', { 
        replace: true, 
        state: { from: location }
      });
    }
    
    // Reset the redirect flag if we do have a session
    if (session) {
      redirectAttempted.current = false;
    }
  }, [session, isLoading, navigate, currentPath]);

  // Show loading indicator ONLY while checking the initial session state
  if (isLoading) {
    if (process.env.NODE_ENV === 'development') {
      console.log('AuthRequired: Waiting for initial session check...');
    }
    // You might want a more integrated loading indicator (e.g., part of layout)
    // But this maintains the blocking behavior during the *initial* check only.
    return <Loading text="Checking authentication..." />;
  }

  // Render children if session exists (profile might still be loading in background)
  if (session) {
    return children;
  }

  // Return null while redirect happens (triggered by useEffect)
  return null; 
}; 